import { Link, useLocation } from "react-router-dom";
import { Shield, Plus, LogOut, Settings, FlaskConical } from "lucide-react";
import { cn } from "@/lib/utils";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import PaywallModal from "./PaywallModal";
import CreditNudge from "./CreditNudge";
import CreditMenu from "./CreditMenu";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const IntelligenceTopBar = () => {
  const { credits, isLoading } = useShieldCredits();
  const [showPricing, setShowPricing] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const tabsRef = useRef<HTMLDivElement>(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const destination = user ? "/dashboard" : "/";
  const userInitial = user?.email?.charAt(0).toUpperCase() || "U";
  const currentPath = location.pathname;

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    supabase.from("profiles").select("role").eq("user_id", user.id).single()
      .then(({ data }) => setIsAdmin(data?.role === "admin"));
  }, [user]);

  const navTabs = [
    { path: "/dashboard", label: "Analyze" },
    { path: "/practice", label: "Practice" },
    { path: "/admin/content-library", label: "Create" },
    { path: "/trends", label: "Insights" },
    { path: "/library", label: "Library" },
    // Secondary
    { path: "/reports", label: "Reports" },
  ];

  // Auto-scroll active tab into view on mobile
  useEffect(() => {
    if (tabsRef.current) {
      const activeTab = tabsRef.current.querySelector('[data-active="true"]');
      if (activeTab) {
        activeTab.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    }
  }, [currentPath]);

  return (
    <>
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-14 gap-2">
          {/* Left: Logo */}
          <Link to={destination} className="flex items-center gap-2 group shrink-0">
            <Shield className="w-5 h-5 text-cyan" />
            <span className="font-bold text-sm tracking-wide text-foreground group-hover:text-cyan transition-colors">
              GreyRock AI<sup className="text-[8px] text-muted-foreground">®</sup>
            </span>
          </Link>

          {/* Center: Nav Tabs */}
          {user && (
            <nav
              ref={tabsRef}
              className="flex items-center gap-1 overflow-x-auto scrollbar-hide mx-2"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {navTabs.map((tab) => {
                const isActive = currentPath === tab.path;
                return (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    data-active={isActive}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide uppercase transition-all whitespace-nowrap shrink-0",
                      isActive
                        ? "text-cyan bg-cyan/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
          )}

          {/* Right: Credit Menu + Settings + User */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Credit Menu (clickable pill with dropdown) */}
            <CreditMenu
              credits={credits}
              isLoading={isLoading}
              onUpgrade={() => setShowPricing(true)}
              onRefill={() => setShowPricing(true)}
            />

            {/* Upgrade CTA on desktop */}
            <button
              onClick={() => setShowPricing(true)}
              className={cn(
                "hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold min-h-[36px]",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.03] transition-transform"
              )}
            >
              <Plus className="w-3.5 h-3.5" />
              <span>{!isLoading && credits <= 3 ? "Upgrade" : "Get Credits"}</span>
            </button>

            {/* Admin links - admin only */}
            {isAdmin && (
              <>
                <Link
                  to="/admin"
                  className={cn(
                    "p-2 rounded-lg transition-colors min-h-[36px] flex items-center justify-center",
                    currentPath === "/admin"
                      ? "text-cyan bg-cyan/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  )}
                  aria-label="Admin Stats"
                  title="Business Stats"
                >
                  <Shield className="w-4 h-4" />
                </Link>
                <Link
                  to="/admin-lab"
                  className={cn(
                    "p-2 rounded-lg transition-colors min-h-[36px] flex items-center justify-center",
                    currentPath === "/admin-lab"
                      ? "text-violet-400 bg-violet-500/10"
                      : "text-muted-foreground hover:text-violet-400 hover:bg-violet-500/10"
                  )}
                  aria-label="Content Lab"
                  title="Content Lab"
                >
                  <FlaskConical className="w-4 h-4" />
                </Link>
              </>
            )}

            {/* Settings gear */}
            {user && (
              <Link
                to="/settings"
                className={cn(
                  "p-2 rounded-lg transition-colors min-h-[36px] flex items-center justify-center",
                  currentPath === "/settings"
                    ? "text-cyan bg-cyan/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                )}
                aria-label="Settings"
              >
                <Settings className="w-4 h-4" />
              </Link>
            )}

            {/* User Menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="focus:outline-none focus:ring-2 focus:ring-cyan/50 rounded-full">
                    <Avatar className="w-8 h-8 border border-border">
                      <AvatarFallback className="text-xs font-semibold bg-muted text-foreground">
                        {userInitial}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                  <div className="px-3 py-2">
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem
                    onClick={() => setShowPricing(true)}
                    className="gap-2 cursor-pointer sm:hidden"
                  >
                    <Plus className="w-4 h-4" />
                    {!isLoading && credits <= 3 ? "Upgrade Plan" : "Buy Credits"}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="gap-2 cursor-pointer">
                    <Link to="/settings">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem
                    onClick={signOut}
                    className="gap-2 text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Low-credit nudge banner */}
        {user && (
          <CreditNudge
            credits={credits}
            onViewPlans={() => setShowPricing(true)}
          />
        )}
      </header>

      <PaywallModal open={showPricing} onOpenChange={setShowPricing} />
    </>
  );
};

export default IntelligenceTopBar;
